import { isAuthSessionError, toErrorMessage } from '../utils/toErrorMessage'
import { supabase } from './supabase'

/** PostgREST default max rows is 1000; page past the cap for complete lists. */
const DEFAULT_PAGE_SIZE = 1000

function asThrownError(error: unknown): Error {
  if (error instanceof Error) return error
  return new Error(toErrorMessage(error, 'Request failed'))
}

async function refreshSessionOnce(): Promise<boolean> {
  const { data, error } = await supabase.auth.refreshSession()
  return !error && !!data.session?.access_token
}

/**
 * Fetch every row from a table by paging with `.range()`.
 * Always orders by a stable column so pages do not skip/duplicate.
 * On JWT/session errors, refreshes the session once and retries the page.
 */
export async function fetchAllFromTable<T>(
  table: string,
  select: string,
  options?: {
    orderBy?: string
    ascending?: boolean
    pageSize?: number
  }
): Promise<T[]> {
  const orderBy = options?.orderBy ?? 'name'
  const ascending = options?.ascending ?? true
  const pageSize = options?.pageSize ?? DEFAULT_PAGE_SIZE
  const all: T[] = []
  let from = 0
  let didRefresh = false

  for (;;) {
    const { data, error } = await supabase
      .from(table)
      .select(select)
      .order(orderBy, { ascending })
      .range(from, from + pageSize - 1)

    if (error) {
      if (!didRefresh && isAuthSessionError(error)) {
        didRefresh = true
        const refreshed = await refreshSessionOnce()
        if (refreshed) continue
      }
      const msg = toErrorMessage(error, 'Request failed')
      if (/statement timeout|57014|canceling statement/i.test(msg)) {
        throw new Error(
          `Could not load the full ${table} list (database timed out on page starting at row ${from + 1}). Try again, or ask with a narrower filter.`
        )
      }
      throw asThrownError(error)
    }

    const chunk = (data ?? []) as T[]
    all.push(...chunk)
    if (chunk.length < pageSize) break
    from += pageSize
  }

  return all
}
